import { ChevronDown, Mail, MapPin, User } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { useWatch } from "react-hook-form";

import {
  AddressFields,
  CheckboxField,
  CountryField,
  DateField,
  fieldClass,
  formatYmd,
  FormRow,
  FormSection,
  FormSubheading,
  PhoneField,
  SelectField,
  TextField
} from "@/components/form";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import type { IndividualEditValues } from "../../../api/individuals";
import type { CaseSettingsForm } from "../useCaseSettingsForm";
import { SettingsLoadingPreview } from "./SettingsLoadingPreview";

/**
 * Shows a bouncing chevron near the footer while the section is scrollable and
 * the reader is still at the top, so the address block below is discoverable.
 */
const useScrollHint = (ref: React.RefObject<HTMLDivElement | null>) => {
  const [visible, setVisible] = useState(false);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const canScroll = el.scrollHeight - el.clientHeight > 8;
    const atTop = el.scrollTop <= 6;
    setVisible(canScroll && atTop);
  }, [ref]);

  useEffect(() => {
    // Layout settles over the first frames (fonts, selects); poll briefly.
    const startedAt = performance.now();
    let raf = requestAnimationFrame(function tick() {
      update();
      if (performance.now() - startedAt < 2000)
        raf = requestAnimationFrame(tick);
    });
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  return { visible, update };
};

/**
 * Pronouns: a canonical select whose "Other" choice reveals a free-text input.
 * Both write the single `pronouns` field.
 */
const PronounsFields: FC<{ form: CaseSettingsForm }> = ({ form }) => {
  const { setValue } = form.form;
  const set = (v: string) =>
    setValue("pronouns", v, { shouldDirty: true, shouldValidate: true });
  return (
    <>
      <Field className={fieldClass}>
        <FieldLabel htmlFor="case-settings-pronouns">Pronouns</FieldLabel>
        <Select
          value={form.selectedPronounsValue || undefined}
          onValueChange={set}
        >
          <SelectTrigger id="case-settings-pronouns" className="w-full">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent position="popper" className="max-h-72">
            {form.options.pronouns.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {form.selectedPronounsValue === "other" ? (
        <Field className={fieldClass}>
          <FieldLabel htmlFor="case-settings-other-pronouns">
            Other Pronouns
          </FieldLabel>
          <Input
            id="case-settings-other-pronouns"
            placeholder="Enter pronouns"
            value={form.customPronounsValue}
            onChange={(e) => set(e.target.value || "other")}
          />
        </Field>
      ) : (
        <div className={fieldClass} aria-hidden />
      )}
    </>
  );
};

export const PersonalInfoSection: FC<{ form: CaseSettingsForm }> = ({
  form
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hint = useScrollHint(scrollRef);
  const similarAddress = useWatch<IndividualEditValues>({
    control: form.form.control,
    name: "similarAddress"
  }) as boolean;

  if (!form.isReady) return <SettingsLoadingPreview variant="personal" />;

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-background">
      <div
        ref={scrollRef}
        onScroll={hint.update}
        className="flex min-h-0 w-full flex-1 flex-col items-start overflow-auto"
      >
        <FormSection
          variant="band"
          icon={User}
          title="Biographical Information"
          className="[&>div:first-child]:border-t-0"
        >
          <FormRow collapseBelow="lg">
            <TextField<IndividualEditValues>
              name="firstName"
              label="First Name"
              placeholder="Enter first name"
              autoComplete="given-name"
            />
            <TextField<IndividualEditValues>
              name="middleName"
              label="Middle Name"
              placeholder="Enter middle name"
              autoComplete="additional-name"
            />
            <TextField<IndividualEditValues>
              name="lastName"
              label="Last Name"
              placeholder="Enter last name"
              autoComplete="family-name"
            />
          </FormRow>

          <FormRow collapseBelow="lg">
            <SelectField<IndividualEditValues>
              name="title"
              label="Title"
              options={form.options.titles}
              clearable
            />
            <PronounsFields form={form} />
          </FormRow>

          <FormRow collapseBelow="lg">
            <DateField<IndividualEditValues>
              name="dob"
              label="Date of Birth"
              max={formatYmd(new Date())}
            />
            <CountryField<IndividualEditValues>
              name="countryOfBirth"
              label="Country Of Birth"
            />
            <CountryField<IndividualEditValues>
              name="countryOfCitizenship"
              label="Country Of Citizenship"
            />
          </FormRow>
        </FormSection>

        <FormSection variant="band" icon={Mail} title="Contact Information">
          <FormRow>
            <TextField<IndividualEditValues>
              name="email"
              label="Email Address"
              type="email"
              placeholder="Enter email address"
              autoComplete="email"
            />
            <PhoneField<IndividualEditValues>
              name="phone"
              label="Phone Number"
            />
          </FormRow>
        </FormSection>

        <FormSection
          variant="band"
          icon={MapPin}
          title="Current Addresses"
          bodyClassName="gap-4"
        >
          <FormSubheading className="text-sm">Physical Address</FormSubheading>
          <AddressFields<IndividualEditValues> name="physicalAddress" />

          <Separator className="my-1" />

          <div className="flex w-full flex-wrap items-center gap-3">
            <FormSubheading className="text-sm">Mailing Address</FormSubheading>
            <CheckboxField<IndividualEditValues>
              name="similarAddress"
              label="Same as physical address"
              className="ml-auto"
            />
          </div>
          <AddressFields<IndividualEditValues>
            name="mailingAddress"
            disabled={similarAddress}
          />
        </FormSection>
      </div>

      {hint.visible ? (
        <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
          <div className="flex size-8 animate-bounce items-center justify-center rounded-full bg-background shadow-lg ring-1 ring-border motion-reduce:animate-none">
            <ChevronDown className="size-4 text-foreground" aria-hidden />
          </div>
        </div>
      ) : null}
    </div>
  );
};
